<template>
  <div class="expandable-card">
    <div
      class="expandable-card-header"
      :class="{ 'is-expanded': isExpanded }"
      @click="toggleExpanded"
    >
      <div class="expandable-card-info">
        <slot name="info" />
      </div>
      <div class="expandable-card-actions">
        <slot name="actions" />
      </div>
      <div class="expandable-card-indicator-container">
        <BIcon
          v-if="closable"
          class="expandable-card-close-indicator"
          @click="handleCloseClick"
        >
          close
        </BIcon>
        <BIcon
          v-if="expandable"
          class="expandable-card-expand-indicator"
          :class="{ 'is-expanded': isExpanded }"
        >
          arrow_drop_up
        </BIcon>
      </div>
    </div>
    <div
      v-if="showExpandMore && expandable && !isExpanded"
      class="expandable-card-expand-more"
    />
    <div
      v-show="isExpanded"
      class="expandable-card-content"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';

type TProps = {
  defaultExpanded?: boolean;
  expandable?: boolean;
  showExpandMore?: boolean;
  closable?: boolean;
};
type TEmit = {
  'click:close': [];
};

const props = withDefaults(defineProps<TProps>(), {
  defaultExpanded: false,
  expandable: true,
  showExpandMore: false,
  closable: false,
});
const emit = defineEmits<TEmit>();

const isExpanded = ref<boolean>(false);
watch(() => props.defaultExpanded, (value) => {
  isExpanded.value = value;
}, { immediate: true });

const toggleExpanded = () => {
  if (!props.expandable) return;
  isExpanded.value = !isExpanded.value;
};
const handleCloseClick = () => {
  if (!props.closable) return;
  emit('click:close');
};
</script>

<style lang="scss" scoped>
.expandable-card {
  margin-bottom: $basespace-200;

  &-header {
    @include m-flex-align-center;
    flex-wrap: nowrap;
    width: 100%;
    background-color: $bgcolor-white;
    padding: $basespace-200 $basespace-300;
    padding-right: $basespace-100;
    transition: $transition-base;

    cursor: pointer;
    &:hover {
      background-color: $bgcolor-dark;
    }
    &.is-expanded {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .expandable-card-info {
      @include m-flex-align-center;
      flex-wrap: nowrap;
      flex-grow: 1;
      min-width: 0;
    }

    .expandable-card-actions {
      @include m-flex-align-center;
      margin-left: auto;
    }

    .expandable-card-indicator-container {
      @include m-flex-center;
      margin-left: $basespace-300;
      margin-right: $basespace-50;

      min-width: 38px;
    }

    .expandable-card-expand-indicator {
      &.is-expanded {
        transform: rotate(180deg);
      }
    }
    .expandable-card-close-indicator {
      margin-right: $basespace-50;
    }
  }

  &-content {
    padding: $basespace-300 $basespace-500;
  }

  &-expand-more {
    height: $basespace-100;
  }

  &-content, &-expand-more {
    border-top: 1px solid $bdcolor-light;
    background-color: $bgcolor-white;
  }
}
</style>
